<script setup lang="ts">
import moment from 'moment';
import { computed, defineProps, withDefaults } from 'vue';
import { useRouter } from 'vue-router';

interface Props {
  pageName?: string
  readAt?: Date | null
  title?: string
  message?: string
  created_at?: Date | null
}

const bgColor = computed(() => {
  if (props.title?.includes('Hooray your content is Approved!')) {
    return 'bg-[--algo-system-green-50]'
  } else if (props.title?.includes('You need to Revise your content!')) {
    return 'bg-[--algo-system-blue-50]'
  } else if (props.title?.includes('Sorry your content is Rejected!')) {
    return 'bg-[--algo-system-red-50]'
  }
  return 'bg-[--algo-accent-gray-50]'
})

const titleColor = computed(() => {
  if (props.title?.includes('Hooray your content is Approved!')) {
    return 'text-[--algo-system-green-600]'
  } else if (props.title?.includes('You need to Revise your content!')) {
    return 'text-[--algo-system-blue-600]'
  } else if (props.title?.includes('Sorry your content is Rejected!')) {
    return 'text-[--algo-system-red-600]'
  }
  return 'text-[--algo-accent-gray-600]'
})

const props = withDefaults(defineProps<Props>(), { pageName: '' })
const router = useRouter()

const emit = defineEmits(['clicked'])
function gotoOrEmit() {
  if (props.pageName == '') {
    emit('clicked', true)
  } else {
    router.push({ name: props.pageName })
  }
}
</script>

<template>
<div 
  class="flex flex-row gap-[12px] p-[12px] border border-[--algo-accent-slate-300] rounded-[6px]"
  :class="bgColor"
  @click="() => gotoOrEmit()"
>
  <div>
    <svg
      v-if="props.readAt == null"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="32" height="32" rx="6" fill="url(#paint0_linear_1339_18605)" />
      <path
        d="M18.4998 22C18.4998 22.1326 18.4472 22.2598 18.3534 22.3536C18.2596 22.4474 18.1325 22.5 17.9998 22.5H13.9998C13.8672 22.5 13.7401 22.4474 13.6463 22.3536C13.5525 22.2598 13.4998 22.1326 13.4998 22C13.4998 21.8674 13.5525 21.7402 13.6463 21.6465C13.7401 21.5527 13.8672 21.5 13.9998 21.5H17.9998C18.1325 21.5 18.2596 21.5527 18.3534 21.6465C18.4472 21.7402 18.4998 21.8674 18.4998 22ZM22.2117 11.77C21.6498 10.6717 20.806 9.74247 19.7667 9.07753C19.7112 9.04206 19.6492 9.01792 19.5843 9.00649C19.5194 8.99505 19.4529 8.99656 19.3886 9.01091C19.3243 9.02527 19.2635 9.0522 19.2096 9.09014C19.1557 9.12809 19.1099 9.1763 19.0747 9.23202C19.0396 9.28774 19.0158 9.34986 19.0047 9.41481C18.9936 9.47976 18.9955 9.54626 19.0102 9.61049C19.0249 9.67471 19.0521 9.7354 19.0904 9.78906C19.1286 9.84272 19.1771 9.88829 19.233 9.92316C20.1246 10.4903 20.847 11.2872 21.3242 12.23C21.3873 12.344 21.4923 12.4288 21.617 12.4665C21.7417 12.5042 21.8761 12.4918 21.9918 12.4318C22.1074 12.3719 22.1951 12.2692 22.2362 12.1456C22.2773 12.022 22.2685 11.8873 22.2117 11.77ZM10.2317 12.5C10.3233 12.5 10.4132 12.4748 10.4914 12.4272C10.5697 12.3796 10.6333 12.3114 10.6755 12.23C11.1527 11.2872 11.8751 10.4903 12.7667 9.92316C12.8226 9.88829 12.8711 9.84272 12.9093 9.78906C12.9476 9.7354 12.9748 9.67471 12.9895 9.61049C13.0042 9.54626 13.0061 9.47976 12.995 9.41481C12.9839 9.34986 12.9601 9.28774 12.925 9.23202C12.8898 9.1763 12.844 9.12809 12.7901 9.09014C12.7362 9.0522 12.6754 9.02527 12.6111 9.01091C12.5468 8.99656 12.4803 8.99505 12.4154 9.00649C12.3505 9.01792 12.2885 9.04206 12.233 9.07753C11.1937 9.74247 10.3499 10.6717 9.78797 11.77C9.7485 11.8462 9.7293 11.9313 9.73222 12.0171C9.73515 12.1028 9.7601 12.1864 9.80468 12.2598C9.84926 12.3331 9.91196 12.3937 9.98675 12.4358C10.0615 12.4779 10.1459 12.5 10.2317 12.5ZM20.9998 15C20.9998 13.674 20.4731 12.4022 19.5354 11.4645C18.5977 10.5268 17.3259 10 15.9998 10C14.6738 10 13.402 10.5268 12.4643 11.4645C11.5266 12.4022 10.9998 13.674 10.9998 15C10.9998 16.6425 10.7011 18.0244 10.1367 18.9963C10.0481 19.1482 10.0012 19.3208 10.0006 19.4966C9.99998 19.6724 10.0458 19.8453 10.1333 19.9978C10.2208 20.1503 10.347 20.2771 10.4992 20.3652C10.6513 20.4534 10.824 20.4999 10.9998 20.5H20.9998C21.1756 20.4997 21.3481 20.453 21.5001 20.3648C21.6521 20.2765 21.7781 20.1498 21.8655 19.9973C21.9529 19.8448 21.9985 19.672 21.9979 19.4963C21.9972 19.3206 21.9503 19.1481 21.8617 18.9963C21.2986 18.0238 20.9998 16.6419 20.9998 15Z"
        fill="#FEFEFE"
      />
      <defs>
        <linearGradient
          id="paint0_linear_1339_18605"
          x1="-0.00243906"
          y1="0.00208156"
          x2="31.9994"
          y2="0.00208156"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#59459B" />
          <stop offset="1" stop-color="#4198D3" />
        </linearGradient>
      </defs>
    </svg>
    <svg
      v-else
      width="34"
      height="34"
      viewBox="0 0 34 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0.5" y="0.5" width="33" height="33" rx="5.5" fill="#FEFEFE" />
      <rect x="0.5" y="0.5" width="33" height="33" rx="5.5" stroke="#CBD5E1" />
      <path
        d="M19.8124 23.75C19.8124 23.8992 19.7532 24.0423 19.6477 24.1477C19.5422 24.2532 19.3991 24.3125 19.2499 24.3125H14.7499C14.6008 24.3125 14.4577 24.2532 14.3522 24.1477C14.2467 24.0423 14.1874 23.8992 14.1874 23.75C14.1874 23.6008 14.2467 23.4577 14.3522 23.3523C14.4577 23.2468 14.6008 23.1875 14.7499 23.1875H19.2499C19.3991 23.1875 19.5422 23.2468 19.6477 23.3523C19.7532 23.4577 19.8124 23.6008 19.8124 23.75ZM23.5988 21.5C23.5012 21.6719 23.3595 21.8147 23.1883 21.9136C23.0171 22.0125 22.8226 22.0639 22.6249 22.0625H11.3749C11.1772 22.0622 10.983 22.0098 10.8119 21.9106C10.6409 21.8114 10.499 21.6688 10.4006 21.4972C10.3022 21.3257 10.2508 21.1312 10.2515 20.9335C10.2522 20.7357 10.305 20.5416 10.4046 20.3708C10.7949 19.6986 11.3749 17.7973 11.3749 15.3125C11.3749 13.8207 11.9676 12.3899 13.0225 11.335C14.0774 10.2801 15.5081 9.6875 16.9999 9.6875C18.4918 9.6875 19.9225 10.2801 20.9774 11.335C22.0323 12.3899 22.6249 13.8207 22.6249 15.3125C22.6249 17.7966 23.2057 19.6986 23.596 20.3708C23.6966 20.5419 23.7498 20.7366 23.7502 20.9351C23.7506 21.1336 23.698 21.3286 23.5981 21.5H23.5988ZM22.6249 20.9375C22.0814 20.0045 21.4999 17.8473 21.4999 15.3125C21.4999 14.119 21.0258 12.9744 20.1819 12.1305C19.338 11.2866 18.1934 10.8125 16.9999 10.8125C15.8065 10.8125 14.6619 11.2866 13.818 12.1305C12.9741 12.9744 12.4999 14.119 12.4999 15.3125C12.4999 17.848 11.9178 20.0052 11.3749 20.9375H22.6249Z"
        fill="#64748B"
      />
    </svg>
  </div>
  <div class="flex flex-col gap-[4px]">
    <div class="text-[18px] font-bold" :class="titleColor">
      {{ props.title }}
    </div>
    <div class="text-[16px] font-normal">
      {{ props.message }}
    </div>
    <div class="text-[14px]">
      {{ moment(props.created_at).utcOffset(7).format('DD-MMM-YYYY HH:mm') }}
    </div>
  </div>
</div>
</template>